// Stock figures in words (spec/13 §6.4): a quantity in its most natural unit ("3.2 kg", "850 ml", "12 cans", "14
// each"), a signed change ("+2.5 kg", "−300 g"), a unit cost per kg, per L or each ("₹72.00/kg", "₹12.50 each"), and a
// level as its word. An `each` item is counted in what it is called ("can", "cans" — the item's `unitName` and
// `unitNamePlural`), or "each" when it has no name. The arithmetic is `units.ts`'; this only puts the venue's number
// grouping and the copy around it.

import type { Money, StockBaseUnit, StockItem, StockLevel, StockUnitCost } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import type { BadgeTone } from '@clavian/ui-web';
import { useCallback, useMemo } from 'react';

import { useCurrentVenue } from '../../shell/scope';
import { useStockItems } from './queries';
import { quantityIn, quantityParts, unitCostPer, type DisplayUnit, type QuantityParts } from './units';

/** The minus sign a figure is set with (U+2212), never a hyphen. */
export const MINUS = '−';
/** What a figure the role may not see, or that does not exist, reads as. */
export const NO_FIGURE = '—';

/** A level's tone: low is a warning, out and below zero are the danger tint; in stock is plain. */
export const LEVEL_TONE: Readonly<Record<StockLevel, BadgeTone>> = { ok: 'neutral', low: 'warn', out: 'danger', negative: 'danger' };

/** What a quantity is counted in: a base unit, or an item with the names of its `each` unit. */
export type UnitLike = StockBaseUnit | Pick<StockItem, 'baseUnit' | 'unitName' | 'unitNamePlural'>;

const baseOf = (unit: UnitLike): StockBaseUnit => (typeof unit === 'string' ? unit : unit.baseUnit);

/** The names of an `each` unit, when it has them. */
function namesOf(unit: UnitLike): { readonly one: string; readonly other: string } | null {
  if (typeof unit === 'string' || unit.unitName === null || unit.unitName === undefined) return null;
  return { one: unit.unitName, other: unit.unitNamePlural ?? unit.unitName };
}

export interface StockFormat {
  /** "3.2 kg", "850 ml", "12 cans", "14 each", "−400 g". */
  quantity(quantity: number, unit: UnitLike): string;
  /** The same in a fixed unit ("0.85 kg" for 850 g). */
  quantityInUnit(quantity: number, unit: DisplayUnit, of?: UnitLike): string;
  /** A change with its sign: "+2.5 kg", "−300 g", "+2 cans". */
  change(quantity: number, unit: UnitLike): string;
  /** "₹72.00/kg", "₹12.50 each"; "—" for none. */
  unitCost(cost: StockUnitCost | null | undefined, unit: UnitLike): string;
  /** "₹1,240.00"; "−₹14.40" with a real minus sign; "—" for none. */
  money(value: Money | null | undefined): string;
  /** A unit's short name: "kg", "g", "L", "ml" — and for pieces the item's plural ("cans") or "each". */
  unitName(unit: DisplayUnit, of?: UnitLike): string;
}

export function useStockFormat(): StockFormat {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  return useMemo(() => {
    const digits = (parts: QuantityParts): string => {
      const [whole = '0', fraction] = parts.digits.split('.');
      const grouped = format.number(Number(whole));
      return fraction === undefined ? grouped : `${grouped}.${fraction}`;
    };
    const words = (parts: QuantityParts, sign: string, of: UnitLike | undefined): string => {
      const value = `${sign}${digits(parts)}`;
      if (parts.unit !== 'each') return t(`stock.unit.${parts.unit}`, { value });
      const names = of === undefined ? null : namesOf(of);
      if (names === null) return t('stock.unit.each', { value });
      return t('stock.unit.named', { value, name: parts.digits === '1' ? names.one : names.other });
    };
    return {
      quantity: (quantity, unit) => {
        const parts = quantityParts(quantity, baseOf(unit));
        return words(parts, parts.negative ? MINUS : '', unit);
      },
      quantityInUnit: (quantity, unit, of) => {
        const parts = quantityIn(quantity, unit);
        return words(parts, parts.negative ? MINUS : '', of);
      },
      change: (quantity, unit) => {
        const parts = quantityParts(quantity, baseOf(unit));
        return words(parts, parts.negative ? MINUS : '+', unit);
      },
      unitCost: (cost, unit) => {
        if (cost === null || cost === undefined) return NO_FIGURE;
        const { price, per } = unitCostPer(cost, baseOf(unit));
        const names = per === 'each' ? namesOf(unit) : null;
        return names === null ? t(`stock.unitCost.${per}`, { price: format.money(price) }) : t('stock.unitCost.named', { price: format.money(price), name: names.one });
      },
      money: (value) => (value === null || value === undefined ? NO_FIGURE : value.amount < 0 ? `${MINUS}${format.money({ ...value, amount: -value.amount })}` : format.money(value)),
      unitName: (unit, of) => {
        const names = unit === 'each' && of !== undefined ? namesOf(of) : null;
        return names === null ? t(`stock.unitName.${unit}`) : names.other;
      },
    };
  }, [t, format]);
}

/**
 * The unit an item at the venue is counted in, by its id — for a line the server answers with only a base unit (a
 * count's line, a delivery's, a report's), so "12 cans" reads the same everywhere. Read from the stock list the screen
 * already holds; `enabled` false keeps a screen that has no other use for it from reading it.
 */
export function useStockUnits(enabled = true): (stockItemId: string, baseUnit: StockBaseUnit) => UnitLike {
  const venue = useCurrentVenue();
  const items = useStockItems(venue.organizationId, venue.id, { enabled });
  const byId = useMemo(() => new Map((items.data ?? []).map((item) => [item.id, item])), [items.data]);
  return useCallback((stockItemId, baseUnit) => byId.get(stockItemId) ?? baseUnit, [byId]);
}
